import { ConnectionItem, Flex, Image, Link, Text } from "@wayflyer/flyui";
import { IconShop24Line } from "@wayflyer/flyui-icons/24/line";
import { useState, type ReactNode } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router";
import { connectByPath, CODAT_PLATFORM_LOGOS } from "../../../data-sources";
import { useCustomerAppConfig } from "undercarriage";

import { usePlatformConnectorProperties } from "../../queries";
import { authStartPath, useConnectionFlowsConfig } from "../../config";
import { COMMON_MESSAGES } from "../../common-messages";
import { ConnectionsList } from "../../common/connections";
import { OAuthErrorBanner } from "../../common/oauth-error-banner";
import { MESSAGES } from "../../messages";

const CONNECTOR_SLUG = "codat_accounting";

export const CodatConnect = ({
  onInitiateConnection,
  fallbackPlatformName,
}: {
  onInitiateConnection?: () => void;
  /** Optional platform name to show when the connector isn't yet connected and no `codat_platform` URL param is set. */
  fallbackPlatformName?: string;
}) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const [connectionInProgress, setConnectionInProgress] = useState(false);
  const [searchParams] = useSearchParams();
  const { connectorsApiUrl, defaultCallbackPath } = useConnectionFlowsConfig();
  const { data: connectorProperties } = usePlatformConnectorProperties(
    customerDomain,
    CONNECTOR_SLUG,
    defaultCallbackPath,
    connectorsApiUrl
  );

  // Prefer the platform name passed via URL param (new picker flow), then fall back to
  // the caller-provided platform name (legacy flow), then the generic connector name.
  const legacyPlatformName = fallbackPlatformName ?? "";
  const selectedPlatformName = searchParams.get("codat_platform") ?? (legacyPlatformName || connectorProperties.name);

  const getPlatformLogo = (name: string) => {
    const logoSrc = CODAT_PLATFORM_LOGOS[name];
    return logoSrc ? <Image src={logoSrc} /> : <IconShop24Line />;
  };

  return (
    <Flex direction="column" gap="8">
      <Flex direction="column" gap="8" align="flex-start" justify="space-between">
        <OAuthErrorBanner connectorSlug={CONNECTOR_SLUG} />
        <Text>{intl.formatMessage(MESSAGES.accountingPlatformDataUsage)}</Text>
        <ConnectionsList
          connectorProperties={connectorProperties}
          renderConnectionLogo={(connection) => getPlatformLogo(connection.identifierName ?? connectorProperties.name)}
          renderConnectAnother={(onConnectAnother) => (
            <Text size="sm">
              {intl.formatMessage(MESSAGES.connectAnotherAccount, {
                link: (chunks: ReactNode) => <Link onClick={onConnectAnother}>{chunks}</Link>,
              })}
            </Text>
          )}
          connectForm={
            <ConnectionItem
              logo={getPlatformLogo(selectedPlatformName)}
              label={selectedPlatformName}
              loading={connectionInProgress}
              actions={[
                {
                  content: intl.formatMessage(COMMON_MESSAGES.connectAccount),
                  variant: "Tertiary",
                  onAction: () => {
                    onInitiateConnection?.();
                    setConnectionInProgress(true);
                    connectByPath(authStartPath(customerDomain, CONNECTOR_SLUG, defaultCallbackPath));
                  },
                },
              ]}
            />
          }
        />
      </Flex>
    </Flex>
  );
};
